import React, {useCallback, useEffect, useState} from 'react';
import {IoIosHeartEmpty} from "react-icons/io";
import {IoHeart} from "react-icons/io5";
import {calculateDiscountedPrice, renderStars} from '../../utils/functions';
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getFetcher } from '../../../api/fetchers';
import { endpoints } from '../../../api/endpoints';
import { BiChevronsRight } from "react-icons/bi";
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import Spinner from '../spinner/Spinner';
import { useCurrency } from '../../context/CurrencyContext';
import { useLanguage } from '../../context/LanguageContext';


interface IProps {
    advertiseId: string | undefined;
}

const ProductsSection: React.FC<IProps> = ({advertiseId}) => {
    const { exchangeRate } = useCurrency();
    const {t} = useLanguage();
    const navigate = useNavigate();
    const [favoriteProductIds, setFavoriteProducts] = useState<Set<string>>(new Set<string>());
    const [clickedProduct] = useState<number | null>(null);
    const [advertiseProducts, setAdvertiseProducts] = useState([]); 
    const {user, addFavoriteProduct, removeFavoriteProduct} = useUser();
    const [loading, setLoading] = useState(false);


     // LIKE PRODUCT TOGGLE
     const toggleLike = useCallback(async (id: string, productName: string) => {
        const isLiked = favoriteProductIds.has(id);
        try {
            if (isLiked) {
                await removeFavoriteProduct(id);
                setFavoriteProducts(prevIds => {
                    const newIds = new Set(prevIds);
                    newIds.delete(id);
                    return newIds;
                });
                toast.info(`${productName} ${t('toastMessage', 'addBasketFavInfo')}`);
            } else {
                await addFavoriteProduct(id);
                setFavoriteProducts(prevIds => {
                    const newIds = new Set(prevIds);
                    newIds.add(id);
                    return newIds;
                });
                toast.success(`${productName}${t('toastMessage', 'addBasketFavSuc')}`);
            }
        } catch (error) {
            toast.error(t('toastMessage', 'addBasketFavErr'));
        }
    }, [favoriteProductIds, addFavoriteProduct, removeFavoriteProduct]);



      // GET PRODUCT BY ADVERTISE ID  
      useEffect(() => {
        if (!advertiseId) return;
        setLoading(true);
        const getProductsByAdvertiseId = async () => {
            try {
                const [productsResponse, favoritesResponse] = await Promise.all([
                    getFetcher(endpoints.advertises.getAdvertiseProduct.replace('{id}', advertiseId)),
                    user ? getFetcher(`${endpoints.user.getFavorites}/${user.id}`) : Promise.resolve(null)
                ]);
    
                if (productsResponse.result) {
                    setAdvertiseProducts(productsResponse.advertiseProducts);
                    setTimeout(() => {
                        setLoading(false);
                    }, 500);
                } else {
                    toast.error(productsResponse.message || t('toastMessage', 'AdsErr'));
                }
    
                if (favoritesResponse?.result) {
                    const favoriteIds = new Set<string>(favoritesResponse.favoriteProducts.map((favoriteProduct: any) => favoriteProduct.productId));
                    setFavoriteProducts(favoriteIds);
                }
            } catch (error) {
                console.error('Reklam ürünleri getirilirken hata oluştu:', error);
            }
        };
    
        getProductsByAdvertiseId();
    }, [advertiseId, user]);

    if(loading) {
        return (
            <div className=' flex w-full h-[300px] justify-center items-center'>
                <Spinner />
            </div>
        )
    }


    return (
        <main className="w-full md:px-12 pt-3"> 
            <section className="flex flex-wrap items-center justify-center mb-24">
                {
                    (advertiseProducts.length > 0 || loading) ? (
                        advertiseProducts.map((item: any) => {
                            const parsedImage = (item.product.images);
                            return(
                                <div key={item.id}>
                                    <div key={item.product.id} className=" w-64 lg:w-80 h-full p-2">
                                        <div className="h-full flex flex-col justify-between overflow-hidden">
                                            <div className="w-full flex justify-center rounded-lg relative py-6 bg-color-product-bg">
                                                <img src={parsedImage[0]} alt={item.product.name} className="h-64 object-cover rounded-t-lg cursor-pointer"  onClick={() => navigate(`/product-detail/${item.product.id}`)}/>
                                                <button className="absolute top-2 right-2 rounded-full bg-transparent"
                                                        onClick={() => toggleLike(item.product.id, item.product.name)}>
                                                    {favoriteProductIds.has(item.product.id) ? (
                                                        <IoHeart className="text-yellow-300 text-3xl mt-2 mr-2"/>
                                                    ) : (
                                                        <IoIosHeartEmpty className="text-yellow-300 text-3xl mt-2 mr-2"/>
                                                    )}
                                                </button>
                                            </div>
                                            <div className="p-4 flex flex-col justify-between flex-grow">
                                                <h2 className="text-lg font-bold truncate cursor-pointer"  onClick={() => navigate(`/product-detail/${item.product.id}`)}>{item.product.name}</h2>
                                                <div className="flex items-center mt-2">
                                                    <div className="flex items-center">
                                                        {renderStars(item.product.rating)}
                                                    </div>
                                                </div>
                                                <div className="w-full mt-4 flex justify-between items-end">
                                                    <div className="flex gap-1 items-center">
                                                        {item.product.discount > 0 ? (
                                                            <>
                                                                <p className="text-lg lg:text-2xl font-semibold ">₺{calculateDiscountedPrice(item.product.price, item.product.discount,0,exchangeRate )}</p>
                                                                <p className="text-sm lg:text-2xl font-semibold line-through text-gray-500">₺{item.product.price}</p>
                                                            </>
                                                        ) : (
                                                            <p className="text-lg lg:text-2xl font-semibold">₺{item.product.price}</p>
                                                        )}
                                                        <div
                                                            className="w-12 h-8 flex items-center justify-center text-sm bg-red-200 text-red-700 px-4 rounded-3xl">
                                                            {item.product.discount}%
                                                        </div>
                                                    </div>
                                                    <button
                                                        className={`text-white min-w-10 text-lg md:text-xl flex justify-center items-center font-bold p-2 rounded-full shadow-md ${clickedProduct === item.product.id ? 'bg-green-500' : 'bg-primary-color'}`}
                                                        onClick={() => navigate(`/product-detail/${item.product.id}`)}>
                                                        <BiChevronsRight/>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border-t border-gray-300 mx-auto mt-16 w-full"></div>
                                </div>
                            )
        
                        })
                    ) : (
                        <div className=' flex w-full justify-center items-center'>
                             <p className=' text-color-bold-gray text-xl font-semibold'>Reklam Ürünü Bulunmamaktadır.</p>
                        </div>
                    )
                }
            </section>
        </main>
    );
};

export default ProductsSection;